import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { BrandConfigReportEndPointLogic } from "../../../../logic/Partners/DataIntegrations/BrandConfigReportEndPointLogic";
import { setBrandConfigReportLoader } from "../../../../features/brandConfigurationReportSlice";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../features/userSlice";

export const useBrandProperties = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const oauthTokenHeader = useSelector(selectUserToken);
  const brandSharedUid = searchParams.get("brandSharedUid");
  const bkInterfaceSharedUid = searchParams.get("bkInterfaceSharedUid");

  const { brandPropertiesData, brandBackendRequirementsData } = useSelector(
    (state: any) => state.brandConfigurationReport,
  );

  // Extract and sort requirements
  const requirements = brandBackendRequirementsData?.[0]?.requirements || [];
  const sortedRequirements = [...requirements].sort(
    (a, b) => (a?.order || 0) - (b?.order || 0),
  );

  const keeperLabels = brandPropertiesData?.[0]?.keeper?.labels || [];
  const brandObjects = brandPropertiesData?.[0]?.objects || [];

  // Helper: Extract placeholder value for key type = pattern
  const extractPlaceholderValue = (
    defaultValue: string,
    fieldValue: string,
  ) => {
    try {
      const defaultObj = JSON.parse(defaultValue);
      const fieldObj = JSON.parse(fieldValue);

      // Find placeholder key
      const placeholderKey = Object.keys(defaultObj).find(
        (key) =>
          typeof defaultObj[key] === "string" &&
          defaultObj[key].includes("$$placeholder"),
      );

      if (!placeholderKey) return null;

      return {
        placeholderKey,
        extractedValue: fieldObj[placeholderKey],
      };
    } catch {
      return null;
    }
  };

  // Helper: Find matching data for requirement
  const findMatchingData = (requirement: any) => {
    const { property_target, key_name } = requirement;

    if (property_target === "keeper") {
      return keeperLabels.find((label: any) => label.label_name === key_name);
    }

    if (
      property_target === "brand_id" ||
      property_target === "raw_output_table_name"
    ) {
      return brandObjects.find(
        (obj: any) =>
          obj.field_type === key_name && obj.object_type === property_target,
      );
    }

    return null;
  };

  // Map requirements to formatted properties
  const formattedBrandProperties = sortedRequirements
    .map((requirement) => {
      const matchedData = findMatchingData(requirement);
      if (!matchedData) return null;

      const {
        key_title,
        key_name,
        key_type,
        requirement_type,
        property_target,
        description,
        default_value,
      } = requirement;

      // Get value based on property target
      let value = matchedData.label_value || matchedData.field_value;
      let placeholder = null;
      let placeholderKey = null;

      // Handle pattern type
      if (key_type === "pattern" && default_value) {
        const extracted = extractPlaceholderValue(
          default_value,
          matchedData.field_value,
        );
        if (extracted) {
          value = extracted.extractedValue;
          placeholder = matchedData.field_value;
          placeholderKey = extracted.placeholderKey;
        }
      }

      const baseProperty = {
        label: key_title,
        value,
        requirementType: requirement_type,
        helperText: `${key_title} is required.`,
        description,
        key_name,
        key_type,
        property_target,
      };

      // Add placeholder fields for pattern type
      return key_type === "pattern"
        ? { ...baseProperty, placeholder, placeholderKey }
        : baseProperty;
    })
    .filter(Boolean);

  // Fetch brand properties and requirements
  const getBrandPropertiesAndRequirements = async () => {
    if (!brandSharedUid || !bkInterfaceSharedUid || !oauthTokenHeader) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Missing required parameters.",
          type: "error",
        }),
      );
      return;
    }

    dispatch(setBrandConfigReportLoader(true));
    try {
      await Promise.all([
        BrandConfigReportEndPointLogic.getBrandConfigurationReport(
          oauthTokenHeader,
        ),
        BrandConfigReportEndPointLogic.getBrandBackendRequirements(
          oauthTokenHeader,
          bkInterfaceSharedUid,
        ),
        BrandConfigReportEndPointLogic.getBrandProperties(
          oauthTokenHeader,
          brandSharedUid,
        ),
      ]);
    } catch (err) {
      console.error("Error fetching brand data:", err);
      dispatch(
        setDisplayErrorSnackBar({
          message:
            "Failed to fetch brand configuration report. Please try again.",
          type: "error",
        }),
      );
    } finally {
      dispatch(setBrandConfigReportLoader(false));
    }
  };

  return {
    formattedBrandProperties,
    getBrandPropertiesAndRequirements,
  };
};
